import { DynamicFormValues } from 'src/app/@shared/form/form.types';

type ExistingContactPersonLookup = {
  firstName?: string;
  lastName?: string;
  person?: unknown;
  personId?: string;
  qualifications?: string[];
  media?: string[];
};

type ContactPersonDynamicRow = DynamicFormValues & {
  legacyContactId?: string | null;
  qualifications?: string[];
  media?: string[];
};

type ContactPersonFreeTextRow = {
  email?: unknown;
  firstName?: unknown;
  hiddenFields?: unknown;
  lastName?: unknown;
  phoneNumber?: unknown;
  sourceContactPersonId?: unknown;
  qualifications?: unknown;
  media?: unknown;
};

const CONTACT_PERSON_RELATION_KEYS = [
  'id',
  'personId',
  'person',
  'phoneNumber',
  'email',
  'hiddenFields',
  'qualifications',
  'media',
  'legacyContactId',
] as const;

function toStringValue(value: unknown): string {
  if (value === null || typeof value === 'undefined') {
    return '';
  }

  return value.toString();
}

function toComparableName(value: unknown): string {
  return toStringValue(value).trim()
    .toLowerCase();
}

function shouldKeepPersonMapping(existingRow: ExistingContactPersonLookup | null, freeTextRow: ContactPersonFreeTextRow): boolean {
  if (!existingRow) {
    return false;
  }

  const existingFirstName = toComparableName(existingRow.firstName);
  const existingLastName = toComparableName(existingRow.lastName);
  const freeTextFirstName = toComparableName(freeTextRow.firstName);
  const freeTextLastName = toComparableName(freeTextRow.lastName);

  // If the mapped person only contains an id (no names), clear mapping to avoid
  // stale/ambiguous preselection and "[object Object]" display in the select field.
  if (!existingFirstName && !existingLastName) {
    return false;
  }

  if (existingFirstName && existingFirstName !== freeTextFirstName) {
    return false;
  }

  if (existingLastName && existingLastName !== freeTextLastName) {
    return false;
  }

  return true;
}

function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry): entry is string => {
    return typeof entry === 'string';
  });
}

function getContactMailingValues(value: { qualifications?: unknown; media?: unknown },
  fallback?: { qualifications?: unknown; media?: unknown }): { qualifications?: string[]; media?: string[] } {
  const result: { qualifications?: string[]; media?: string[] } = {};

  for (const key of ['qualifications', 'media'] as const) {
    const rawValue = Array.isArray(value[key])
      ? value[key]
      : fallback?.[key];

    if (Array.isArray(rawValue)) {
      result[key] = normalizeStringArray(rawValue);
    }
  }

  return result;
}

function getExistingPersonLookup(existingRows: unknown[]): Record<string, ExistingContactPersonLookup> {
  const rowsById: Record<string, ExistingContactPersonLookup> = {};

  for (const row of existingRows) {
    if (!row || typeof row !== 'object') {
      continue;
    }

    const value = row as Record<string, unknown>;
    const id = toStringValue(value.id);

    if (!id) {
      continue;
    }

    rowsById[id] = {
      ...getContactMailingValues(value),
      firstName: toStringValue((value.person as Record<string, unknown>)?.firstName),
      lastName: toStringValue((value.person as Record<string, unknown>)?.lastName),
      person: value.person,
      personId: toStringValue(value.personId) || undefined, // eslint-disable-line no-undefined
    };
  }

  return rowsById;
}

export function mapContactPersonsFreeTextToContactPersonsRows(freeTextRows: unknown,
  existingContactPersons: unknown[] = []): DynamicFormValues[] {
  if (!Array.isArray(freeTextRows)) {
    return [];
  }

  const existingRowsById = getExistingPersonLookup(existingContactPersons);

  return freeTextRows.map((row) => {
    const value: ContactPersonFreeTextRow = row && typeof row === 'object'
      ? row as ContactPersonFreeTextRow
      : {};

    const sourceContactPersonId = toStringValue(value.sourceContactPersonId);
    const existingRow = sourceContactPersonId
      ? existingRowsById[sourceContactPersonId]
      : null;
    const shouldKeepPerson = shouldKeepPersonMapping(existingRow, value);

    return {
      ...getContactMailingValues(value, existingRow),
      email: toStringValue(value.email),
      firstName: toStringValue(value.firstName),
      hiddenFields: normalizeStringArray(value.hiddenFields),
      id: sourceContactPersonId || null,
      lastName: toStringValue(value.lastName),
      person: shouldKeepPerson
        ? existingRow.person || null
        : null,
      personId: shouldKeepPerson
        ? existingRow.personId || null
        : null,
      phoneNumber: toStringValue(value.phoneNumber),
    };
  });
}

export function mapContactPersonsRowsToFreeText(rows: unknown): DynamicFormValues[] {
  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.map((row) => {
    const value = row && typeof row === 'object'
      ? row as Record<string, unknown>
      : {};

    return {
      ...getContactMailingValues(value),
      email: toStringValue(value.email),
      firstName: toStringValue(value.firstName),
      hiddenFields: normalizeStringArray(value.hiddenFields),
      lastName: toStringValue(value.lastName),
      phoneNumber: toStringValue(value.phoneNumber),
      sourceContactPersonId: toStringValue(value.id),
    };
  });
}

// eslint-disable-next-line max-lines-per-function,max-statements
export function sanitizeContactPersonsRows(rows: unknown): DynamicFormValues[] {
  if (!Array.isArray(rows)) {
    return [];
  }

  const sanitizedRows: ContactPersonDynamicRow[] = [];

  for (const row of rows) {
    if (!row || typeof row !== 'object') {
      continue;
    }

    const value = row as Record<string, unknown>;
    const sanitized: ContactPersonDynamicRow = {};

    for (const key of CONTACT_PERSON_RELATION_KEYS) {
      if (!(key in value)) {
        continue;
      }

      if (['hiddenFields', 'qualifications', 'media'].includes(key)) {
        sanitized[key] = normalizeStringArray(value[key]);
        continue;
      }

      if (key === 'person') {
        const personValue = value.person as Record<string, unknown>;

        if (personValue?.id && typeof personValue.id === 'string') {
          sanitized.person = {
            id: personValue.id,
          };
        }

        continue;
      }

      if (key === 'legacyContactId') {
        const legacyContactId = toStringValue(value.legacyContactId);

        sanitized.legacyContactId = legacyContactId || null;
        continue;
      }

      if (key === 'personId') {
        if (value.personId === null) {
          sanitized.personId = null;
          continue;
        }

        const personId = toStringValue(value.personId);

        if (personId) {
          sanitized.personId = personId;
        }

        continue;
      }

      const scalarValue = toStringValue(value[key]);

      if (scalarValue) {
        sanitized[key] = scalarValue;
      }
    }

    if (Object.keys(sanitized).length) {
      sanitizedRows.push(sanitized);
    }
  }

  return sanitizedRows;
}
